import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useMutation } from '@tanstack/react-query'
import { IconCloseDefault } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { NavBar } from '../../components'
import { BoxContainer } from '../../components/BoxContainer'
import { useCheckoutData } from '../../components/CheckoutProvider/CheckoutProvider'
import { useFreeTrialData } from '../../hooks'
import { useCheckEmail } from '../../hooks/auth'
import { useAuthEvents, usePageLoadEvents } from '../../hooks/tracking'
import { useNonCommerceGoldStandardEvents } from '../../hooks/tracking/useNonCommerceGoldStandardEvents'
import { Auth0Service } from '../../services/Auth0Service'
import { useCtaPreserveQueryParams } from '../../utils/cta'
import { isCoachPrePaywallPath, useCheckoutPath } from '../../utils/paths'
import {
  MAX_EMAIL_LENGTH,
  MAX_EMAIL_LOCAL_PART_LENGTH,
} from '../../utils/validations'
import { EmailStep } from './EmailStep'
import { PasswordStep } from './PasswordStep'

export const LoginSteps = ({ errorMessage }: { errorMessage?: string }) => {
  const { t } = useTranslation('@mc/persona')
  const getUrlWithQueryParams = useCtaPreserveQueryParams()

  const {
    mutate: handleLogin,
    isLoading,
    error: err,
    isSuccess,
    reset,
  } = useMutation(Auth0Service.login, {
    onSuccess: ({ redirect }: { redirect: string }) => {
      window.location.href = redirect
    },
  })
  const loginError = err as { message: string }

  const { query, push, back, pathname } = useRouter()
  const basePath = isCoachPrePaywallPath(pathname) ? '/coach' : ''
  const { email: contextEmail, setEmail } = useCheckoutData()
  const email = (query.email as string) || contextEmail
  const comesFromSignup = query.from === 'signup' && email
  const isSocial = comesFromSignup && query.social === 'true'
  const [step, setStep] = useState<'email' | 'password'>(
    query.step === 'password' || (comesFromSignup && !isSocial)
      ? 'password'
      : 'email',
  )
  const [emailError, setEmailError] = useState<string | undefined>(undefined)
  const checkoutPath = useCheckoutPath()
  const { freeTrialSuffix } = useFreeTrialData()

  const { page } = useAuthEvents({ step: step === 'email' ? 1 : 2 })
  const { trackCartEmailCaptured } = useNonCommerceGoldStandardEvents('login')

  usePageLoadEvents({
    source: 'login',
    trackGSPageLoad: true,
    trackSignViewed: true,
    page:
      step === 'email'
        ? `step one login: insert email or social ${freeTrialSuffix}`
        : `step one login: enter password ${freeTrialSuffix}`,
  })

  useEffect(() => {
    if (query.email) {
      setEmail(query.email as string)
    }
  }, [query.email, setEmail])

  const handleSetPasswordStep = () => {
    setStep('password')
  }

  const handlePasswordSubmit = ({ password }: { password?: string }) => {
    if (password && email) {
      const redirectUrl = query.redirect_url as string | undefined
      const fullRedirectUrl = redirectUrl
        ? `${window.location.origin}${redirectUrl}`
        : window.location.href

      handleLogin({
        email,
        password,
        returnTo: encodeURIComponent(fullRedirectUrl),
        basePath,
      })
    }
  }

  const {
    checkEmail,
    checkEmailMutation,
    isLoadingCheckEmail,
    resetCheckEmail,
  } = useCheckEmail({
    onSuccess: (res) => {
      if (
        res.identityProviders?.length === 1 &&
        res.identityProviders?.includes('email')
      ) {
        handleSetPasswordStep()
      }
    },
    onError: (error: { status: number }) => {
      if (error?.status === 404) {
        trackCartEmailCaptured({ email })
        push(getUrlWithQueryParams(checkoutPath))
      } else if (error?.status === 400) {
        setEmailError(
          t('auth.login.emailFormatError', {
            maxLength: MAX_EMAIL_LENGTH,
            maxLocal: MAX_EMAIL_LOCAL_PART_LENGTH,
          }),
        )
      }
    },
  })

  const handleEmailSubmit = ({ email: emailSubmit }: { email?: string }) => {
    if (emailSubmit) {
      setEmailError(undefined)
      if (emailSubmit !== email || email) {
        checkEmailMutation({ email: emailSubmit })
      } else if (checkEmail?.identityProviders?.includes('email')) {
        handleSetPasswordStep()
      }
      setEmail(emailSubmit)
    }
  }

  const handleClickBack = () => {
    resetCheckEmail()
    reset()
    setStep('email')
  }

  return (
    <>
      <div className='d-flex d-md-none row no-gutters mc-p-6 align-items-center justify-content-end'>
        <button className='mc-clickable' onClick={back}>
          <IconCloseDefault
            size='sm'
            style={{ color: 'var(--mc-color-icon-tint-dark)' }}
          />
        </button>
      </div>
      <NavBar pageName={page} showLogin={false} source='login' />
      <BoxContainer>
        {step === 'email' && (
          <EmailStep
            initialValues={{ email }}
            onEmailSubmit={handleEmailSubmit}
            onChangeEmail={() => {
              setEmailError(undefined)
            }}
            providers={checkEmail?.identityProviders}
            isLoading={!!email && isLoadingCheckEmail}
            showErrorBox={true}
            emailError={emailError}
            errorMessage={errorMessage}
          />
        )}
        {step === 'password' && (
          <PasswordStep
            onPasswordSubmit={handlePasswordSubmit}
            email={email}
            onClickBack={handleClickBack}
            isLoading={isLoading || isSuccess}
            error={loginError?.message && t('auth.login.defaultError')}
          />
        )}
      </BoxContainer>
    </>
  )
}
